import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
    padding: 20px;
`;

const ModalContent = styled.div`
    background: #1a1a3e;
    border: 2px solid rgba(244, 212, 163, 0.3);
    border-radius: 16px;
    padding: 30px;
    max-width: 900px;
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;
    color: #f4d4a3;

    @media (max-width: 768px) {
        padding: 20px;
        max-height: 95vh;
    }
`;

const ModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 30px;
    padding-bottom: 20px;
    border-bottom: 2px solid rgba(244, 212, 163, 0.2);
`;

const ModalTitle = styled.h2`
    font-size: 2rem;
    font-weight: 700;
    color: #f4d4a3;
    margin: 0;

    @media (max-width: 768px) {
        font-size: 1.5rem;
    }
`;

const CloseButton = styled.button`
    background: rgba(244, 212, 163, 0.1);
    border: 2px solid rgba(244, 212, 163, 0.3);
    border-radius: 8px;
    color: #f4d4a3;
    font-size: 1.5rem;
    width: 40px;
    height: 40px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;

    &:hover {
        background: rgba(244, 212, 163, 0.2);
        border-color: rgba(244, 212, 163, 0.5);
    }
`;

const SectionTitle = styled.h3`
    font-size: 1.3rem;
    margin-bottom: 20px;
    color: #f4d4a3;
    font-weight: 600;
`;

const GojuonTable = styled.div`
    display: flex;
    flex-direction: column;
    gap: 15px;
`;

const Row = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;

    @media (max-width: 768px) {
        gap: 8px;
    }
`;

const RowToggleButton = styled.button`
    padding: 10px 18px;
    background: rgba(244, 212, 163, 0.1);
    border: 2px solid rgba(244, 212, 163, 0.3);
    border-radius: 8px;
    color: #f4d4a3;
    font-size: 1.1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    min-width: 70px;
    white-space: nowrap;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        background: rgba(244, 212, 163, 0.2);
        border-color: rgba(244, 212, 163, 0.5);
    }

    ${props => props.allSelected && `
        background: rgba(244, 212, 163, 0.3);
        border-color: rgba(244, 212, 163, 0.6);
    `}

    @media (max-width: 768px) {
        padding: 8px 14px;
        font-size: 1rem;
        min-width: 65px;
    }
`;

const CharacterGrid = styled.div`
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    flex: 1;

    @media (max-width: 768px) {
        gap: 6px;
    }
`;

const CharacterButton = styled.button`
    padding: 12px 16px;
    background: ${props => props.selected ? 'rgba(244, 212, 163, 0.3)' : 'rgba(244, 212, 163, 0.1)'};
    border: 2px solid ${props => props.selected ? 'rgba(244, 212, 163, 0.6)' : 'rgba(244, 212, 163, 0.3)'};
    border-radius: 8px;
    color: #f4d4a3;
    font-size: 1.2rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    min-width: 50px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;

    &:hover {
        background: ${props => props.selected ? 'rgba(244, 212, 163, 0.4)' : 'rgba(244, 212, 163, 0.2)'};
        border-color: rgba(244, 212, 163, 0.5);
        transform: translateY(-2px);
    }

    ${props => !props.selected && `
        opacity: 0.5;
    `}

    @media (max-width: 768px) {
        padding: 8px 12px;
        font-size: 1rem;
        min-width: 45px;
    }
`;

const ActionButtons = styled.div`
    display: flex;
    gap: 15px;
    margin-top: 30px;
    padding-top: 20px;
    border-top: 2px solid rgba(244, 212, 163, 0.2);
    justify-content: flex-end;

    @media (max-width: 768px) {
        flex-direction: column;
    }
`;

const ActionButton = styled.button`
    padding: 12px 30px;
    background: rgba(244, 212, 163, 0.1);
    border: 2px solid rgba(244, 212, 163, 0.3);
    border-radius: 8px;
    color: #f4d4a3;
    font-size: 1.1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    font-family: inherit;

    &:hover {
        background: rgba(244, 212, 163, 0.2);
        border-color: rgba(244, 212, 163, 0.5);
        transform: translateY(-2px);
    }

    ${props => props.primary && `
        background: rgba(244, 212, 163, 0.3);
        border-color: rgba(244, 212, 163, 0.6);
    `}

    @media (max-width: 768px) {
        width: 100%;
    }
`;

const QuestionCountSection = styled.div`
    margin-top: 30px;
    padding-top: 20px;
    border-top: 2px solid rgba(244, 212, 163, 0.2);
`;

const QuestionCountLabel = styled.label`
    display: block;
    font-size: 1rem;
    margin-bottom: 10px;
    color: #f4d4a3;
    opacity: 0.9;
`;

const QuestionCountDescription = styled.p`
    font-size: 0.9rem;
    margin-bottom: 15px;
    opacity: 0.7;
    color: #f4d4a3;
`;

const QuestionCountSelect = styled.select`
    padding: 12px 15px;
    background: rgba(244, 212, 163, 0.1);
    border: 2px solid rgba(244, 212, 163, 0.3);
    border-radius: 8px;
    color: #f4d4a3;
    font-size: 1.1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    font-family: inherit;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23f4d4a3' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 15px center;
    padding-right: 40px;
    width: 100%;
    max-width: 300px;

    &:hover {
        background-color: rgba(244, 212, 163, 0.2);
        border-color: rgba(244, 212, 163, 0.5);
    }

    &:focus {
        outline: none;
        border-color: rgba(244, 212, 163, 0.6);
        background-color: rgba(244, 212, 163, 0.2);
    }

    option {
        background: #1a1a3e;
        color: #f4d4a3;
    }
`;

const CheckboxSection = styled.div`
    margin-top: 30px;
    padding-top: 20px;
    border-top: 2px solid rgba(244, 212, 163, 0.2);
`;

const CheckboxLabel = styled.label`
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 1rem;
    color: #f4d4a3;
    cursor: pointer;
    opacity: 0.9;
`;

const CheckboxInput = styled.input`
    width: 20px;
    height: 20px;
    cursor: pointer;
    accent-color: rgba(244, 212, 163, 0.6);
`;

const CheckboxDescription = styled.p`
    font-size: 0.9rem;
    margin-top: 8px;
    margin-left: 32px;
    opacity: 0.7;
    color: #f4d4a3;
`;

const SelectAllButtons = styled.div`
    display: flex;
    gap: 15px;
    margin-top: 20px;
    margin-bottom: 10px;
    justify-content: flex-start;
    flex-wrap: wrap;

    @media (max-width: 768px) {
        flex-direction: column;
        gap: 10px;
    }
`;

const SelectAllButton = styled.button`
    padding: 10px 20px;
    background: rgba(244, 212, 163, 0.1);
    border: 2px solid rgba(244, 212, 163, 0.3);
    border-radius: 8px;
    color: #f4d4a3;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    font-family: inherit;

    &:hover {
        background: rgba(244, 212, 163, 0.2);
        border-color: rgba(244, 212, 163, 0.5);
        transform: translateY(-2px);
    }

    @media (max-width: 768px) {
        width: 100%;
    }
`;

// 五十音数据，按行组织
const gojuonRows = [
    { row: 'あ', characters: ['あ', 'い', 'う', 'え', 'お'] },
    { row: 'か', characters: ['か', 'き', 'く', 'け', 'こ'] },
    { row: 'さ', characters: ['さ', 'し', 'す', 'せ', 'そ'] },
    { row: 'た', characters: ['た', 'ち', 'つ', 'て', 'と'] },
    { row: 'な', characters: ['な', 'に', 'ぬ', 'ね', 'の'] },
    { row: 'は', characters: ['は', 'ひ', 'ふ', 'へ', 'ほ'] },
    { row: 'ま', characters: ['ま', 'み', 'む', 'め', 'も'] },
    { row: 'や', characters: ['や', 'ゆ', 'よ'] },
    { row: 'ら', characters: ['ら', 'り', 'る', 'れ', 'ろ'] },
    { row: 'わ', characters: ['わ', 'を', 'ん'] },
];

const GojuonSettings = ({ isOpen, onClose, onSave, selectedCharacters: initialSelected, questionCount: initialQuestionCount, retryMissedQuestions: initialRetryMissedQuestions, randomOrder: initialRandomOrder }) => {
    const { t } = useTranslation();
    const [selectedCharacters, setSelectedCharacters] = useState(new Set(initialSelected || []));
    const [questionCount, setQuestionCount] = useState(initialQuestionCount || 10);
    const [retryMissedQuestions, setRetryMissedQuestions] = useState(initialRetryMissedQuestions !== undefined ? initialRetryMissedQuestions : false);
    const [randomOrder, setRandomOrder] = useState(initialRandomOrder !== undefined ? initialRandomOrder : true);

    useEffect(() => {
        if (isOpen && initialSelected) {
            setSelectedCharacters(new Set(initialSelected));
        }
        if (isOpen && initialQuestionCount) {
            setQuestionCount(initialQuestionCount);
        }
        if (isOpen && initialRetryMissedQuestions !== undefined) {
            setRetryMissedQuestions(initialRetryMissedQuestions);
        }
        if (isOpen && initialRandomOrder !== undefined) {
            setRandomOrder(initialRandomOrder);
        }
    }, [isOpen, initialSelected, initialQuestionCount, initialRetryMissedQuestions, initialRandomOrder]);

    const toggleCharacter = (char) => {
        const newSelected = new Set(selectedCharacters);
        if (newSelected.has(char)) {
            newSelected.delete(char);
        } else {
            newSelected.add(char);
        }
        setSelectedCharacters(newSelected);
    };

    const toggleRow = (rowChars) => {
        const newSelected = new Set(selectedCharacters);
        const allSelected = rowChars.every(char => newSelected.has(char));
        
        if (allSelected) {
            // 如果全部选中，则全部取消
            rowChars.forEach(char => newSelected.delete(char));
        } else {
            // 否则全部选中
            rowChars.forEach(char => newSelected.add(char));
        }
        setSelectedCharacters(newSelected);
    };

    const selectAll = () => {
        const allChars = gojuonRows.flatMap(row => row.characters);
        setSelectedCharacters(new Set(allChars));
    };

    const deselectAll = () => {
        setSelectedCharacters(new Set());
    };

    const handleSave = () => {
        onSave(Array.from(selectedCharacters), questionCount, retryMissedQuestions, randomOrder);
        onClose();
    };

    if (!isOpen) return null;

    const someSelected = Array.from(selectedCharacters).length > 0;

    return (
        <ModalOverlay onClick={onClose}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
                <ModalHeader>
                    <ModalTitle>{t('wordGame.settings.title')}</ModalTitle>
                    <CloseButton onClick={onClose}>×</CloseButton>
                </ModalHeader>

                <SectionTitle>{t('wordGame.settings.characterSelection')}</SectionTitle>
                
                <GojuonTable>
                    {gojuonRows.map((rowData, rowIndex) => {
                        const rowSelected = rowData.characters.every(char => selectedCharacters.has(char));
                        
                        return (
                            <Row key={rowIndex}>
                                <RowToggleButton
                                    onClick={() => toggleRow(rowData.characters)}
                                    allSelected={rowSelected}
                                >
                                    {rowData.row}行
                                </RowToggleButton>
                                <CharacterGrid>
                                    {rowData.characters.map((char, charIndex) => (
                                        <CharacterButton
                                            key={charIndex}
                                            selected={selectedCharacters.has(char)}
                                            onClick={() => toggleCharacter(char)}
                                        >
                                            <span>{char}</span>
                                        </CharacterButton>
                                    ))}
                                </CharacterGrid>
                            </Row>
                        );
                    })}
                </GojuonTable>

                <SelectAllButtons>
                    <SelectAllButton onClick={selectAll}>
                        {t('wordGame.settings.selectAll')}
                    </SelectAllButton>
                    <SelectAllButton onClick={deselectAll}>
                        {t('wordGame.settings.deselectAll')}
                    </SelectAllButton>
                </SelectAllButtons>

                <QuestionCountSection>
                    <QuestionCountLabel>{t('wordGame.settings.questionCount')}</QuestionCountLabel>
                    <QuestionCountDescription>{t('wordGame.settings.questionCountDescription')}</QuestionCountDescription>
                    <QuestionCountSelect
                        value={questionCount}
                        onChange={(e) => {
                            const value = e.target.value;
                            setQuestionCount(value === 'all' ? 'all' : Number(value));
                        }}
                    >
                        {[5, 10, 15, 20, 25, 30, 40, 50].map(num => (
                            <option key={num} value={num}>{num}</option>
                        ))}
                        <option value="all">{t('wordGame.settings.allQuestions')}</option>
                    </QuestionCountSelect>
                </QuestionCountSection>

                <CheckboxSection>
                    <CheckboxLabel>
                        <CheckboxInput
                            type="checkbox"
                            checked={retryMissedQuestions}
                            onChange={(e) => setRetryMissedQuestions(e.target.checked)}
                        />
                        <span>{t('wordGame.settings.retryMissedQuestions')}</span>
                    </CheckboxLabel>
                    <CheckboxDescription>
                        {t('wordGame.settings.retryMissedQuestionsDescription')}
                    </CheckboxDescription>
                </CheckboxSection>

                <CheckboxSection>
                    <CheckboxLabel>
                        <CheckboxInput
                            type="checkbox"
                            checked={randomOrder}
                            onChange={(e) => setRandomOrder(e.target.checked)}
                        />
                        <span>{t('wordGame.settings.randomOrder')}</span>
                    </CheckboxLabel>
                    <CheckboxDescription>
                        {t('wordGame.settings.randomOrderDescription')}
                    </CheckboxDescription>
                </CheckboxSection>

                <ActionButtons>
                    <ActionButton onClick={onClose}>
                        {t('wordGame.settings.cancel')}
                    </ActionButton>
                    <ActionButton primary onClick={handleSave} disabled={!someSelected}>
                        {t('wordGame.settings.save')}
                    </ActionButton>
                </ActionButtons>
            </ModalContent>
        </ModalOverlay>
    );
};

export default GojuonSettings;

